import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../../../ui/modal/Modal';
import { SelectStatic } from '../../../ui/select/Select';
import Textarea from '../../../ui/textarea/Textarea';
import Input from '../../../ui/input/Input';
import Checkbox from '../../../ui/checkbox/Checkbox';
import Button, { ButtonThemes } from '../../../ui/button/Button';
import Alert from '../../../ui/alert/Alert';
import { Icon } from '../../../ui/icon/Icon';
import { Icons } from '../../../ui/icon/icons/material';
import { IconStyles } from '../../../ui/icon/Icon.types';
import styles from './HelpdeskCaseModal.module.scss';
import { PRODUCTS } from '../../../common/constants/products';
import createHelpdeskCase, { CreateHelpdeskCaseRequest, CreateHelpdeskCaseResponse, HelpdeskSupportType } from '../../../services/helpdeskService';
import { useChatContext } from '../../../features/chat/contexts/ChatContext';
import { useAuth } from '../../../contexts/auth/AuthContext';
import { getClassNames } from '../../../helpers/classHelpers';

export type HelpdeskModalProps = {
    chatTitle: string;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (response?: CreateHelpdeskCaseResponse) => void;
};

type ConversationMessageRole = 'user' | 'assistant';

type ConversationMessage = {
    role: ConversationMessageRole;
    content: string;
};

const SUPPORT_TYPE: HelpdeskSupportType = 'ProductTechnicalSupport';

const getConversationMessageRole = (message: unknown): ConversationMessageRole | undefined => {
    if (!message || typeof message !== 'object') {
        return undefined;
    }

    if ('role' in message && typeof message.role === 'string') {
        if (message.role === 'user') return 'user';
        if (message.role === 'assistant') return 'assistant';
    }

    if ('type' in message && typeof message.type === 'string') {
        if (message.type === 'human') return 'user';
        if (message.type === 'ai') return 'assistant';
    }

    return undefined;
};

const HelpdeskCaseModal: React.FC<HelpdeskModalProps> = ({ chatTitle, isOpen, onClose, onSuccess }) => {
    const { value: chatContextValue } = useChatContext();
    const [selectedProduct, setSelectedProduct] = useState<string>(String(PRODUCTS[0]?.value ?? ''));
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [userToken] = useState('');
    const [isIncludeChatResponse, setIsIncludeChatResponse] = useState(true);
    const [fieldErrors, setFieldErrors] = useState<{ subject?: string; description?: string }>({});
    const [submitError, setSubmitError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { getSalesforceToken } = useAuth();
    const options = useMemo(() => PRODUCTS, []);
    const conversationMessages = useMemo(() => {
        const messages = chatContextValue?.chat?.messages;
        if (!Array.isArray(messages)) {
            return [] as ConversationMessage[];
        }

        const parsedMessages: ConversationMessage[] = [];
        for (const message of messages) {
            const role = getConversationMessageRole(message);
            if (!role) {
                continue;
            }

            if (!('content' in message) || typeof message.content !== 'string') {
                continue;
            }

            parsedMessages.push({
                role,
                content: message.content,
            });
        }

        return parsedMessages;
    }, [chatContextValue?.chat?.messages]);

    const conversationTurns = useMemo(() => {
        const turns: NonNullable<CreateHelpdeskCaseRequest['conversationTurns']> = [];
        let pendingUserRequest = '';

        for (const message of conversationMessages) {
            if (message.role === 'user') {
                pendingUserRequest = message.content;
                continue;
            }

            turns.push({
                userRequest: pendingUserRequest,
                assistantResponse: message.content,
            });
            pendingUserRequest = '';
        }

        if (pendingUserRequest) {
            turns.push({ userRequest: pendingUserRequest });
        }

        return turns;
    }, [conversationMessages]);
    useEffect(() => {
        if (isOpen) {
            setSubject(chatTitle);
            setSubmitError('');
            setFieldErrors({});
        }
    }, [chatTitle, isOpen]);

    const handleSubmit = useCallback(async () => {
        if (isSubmitting) return;

        const trimmedSubject = subject.trim();
        const trimmedDescription = description.trim();

        const errors: { subject?: string; description?: string } = {};
        if (!trimmedSubject) {
            errors.subject = 'Subject is required';
        }
        if (!trimmedDescription) {
            errors.description = 'Description is required';
        }

        setFieldErrors(errors);
        if (Object.keys(errors).length) {
            return;
        }

        setSubmitError('');
        setIsSubmitting(true);

        const payload: CreateHelpdeskCaseRequest = {
            subject: trimmedSubject,
            description: trimmedDescription,
            product: selectedProduct,
            supportType: SUPPORT_TYPE,
            includeChatResponse: isIncludeChatResponse,
            conversationTurns,
            userToken: userToken.trim(),
        };
        try {
            const response = await createHelpdeskCase(payload,getSalesforceToken);
            onClose();
            // The API may return plain text in some environments, so only forward structured case data when available.
            onSuccess?.(typeof response === 'string' ? undefined : response);
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : String(error));
        } finally {
            setIsSubmitting(false);
        }
    }, [isSubmitting, subject, description, selectedProduct, isIncludeChatResponse, conversationTurns, userToken, getSalesforceToken, onClose, onSuccess]);

    const header = (
        <div className={getClassNames([styles['header']])}>
            <div className={styles['header-icon']}>
                <Icon.Base title={'Help'} iconName={Icons.assignment} iconStyle={IconStyles.filled} className={styles['icon-wrapper']} />
            </div>
            <div className={styles.title}>Starting Helpdesk Case</div>
        </div>
    );

    const body = (
        <div className={styles.modalBody}>
            <p className={styles.description}>
                Enter your question to create a support case.
                <br />
                You can choose to include chat information to help us understand your case faster.
            </p>

            <div className={styles.form}>
                <div className={styles.field}>
                    <Input
                        withUIState={false}
                        label={'Subject*'}
                        value={subject}
                        onChange={setSubject}
                        placeholder={'Enter case subject'}
                        maxLength={255}
                        hasError={!!fieldErrors.subject}
                        helperText={fieldErrors.subject}
                    />
                </div>

                <div className={styles.field}>
                    <SelectStatic
                        withUIState={false}
                        label={'Product*'}
                        value={selectedProduct}
                        options={options}
                        onChange={(v: string) => setSelectedProduct(v)}
                    />
                </div>

                <div className={styles.field}>
                    <Textarea
                        withUIState={false}
                        label={undefined}
                        placeholder={'Describe your issue in detail...'}
                        value={description}
                        rows={6}
                        onChange={setDescription}
                        isInvalid={!!fieldErrors.description}
                    />
                    {fieldErrors.description && <Alert type={'error'} className={styles.fieldError} headline={fieldErrors.description} />}
                </div>

                {/* <div className={styles.field}>
                    <Input
                        withUIState={false}
                        label={'x-user-token (for testing)'}
                        value={userToken}
                        onChange={setUserToken}
                        placeholder={'Paste external PI token here'}
                    />
                </div> */}

                <div className={styles.checkboxWrapper}>
                    <Checkbox withUIState={false} label={'Include chat history with helpdesk case'} isChecked={isIncludeChatResponse} onChange={(v) => setIsIncludeChatResponse(v)} />
                </div>

                {submitError && (
                    <Alert type={'error'} className={styles.errorMessage} headline={submitError} />
                )}
            </div>
        </div>
    );

    const footer = (
        <div className={styles.footer}>
            <Button theme={ButtonThemes.primary} isLoading={isSubmitting} onClick={handleSubmit}>Submit</Button>
        </div>
    );

    return (
        <Modal className={styles.helpdeskModal} isOpen={isOpen} header={header} body={body} footer={footer} onClose={onClose} closeOnBackdropClick={false} closeOnEscape />
    );
};

export default HelpdeskCaseModal;
